import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { useContent } from "@/context/ContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-white border border-[#122A1D]/15 focus:border-[#C8A24A] focus:outline-none text-[#122A1D] placeholder-[#5B6F64]/70 px-5 py-4 font-light transition-colors duration-300";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", service: "", message: "" });
  const [sending, setSending] = useState(false);
  const { content } = useContent();
  const c = content?.contact;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name,
        email: form.email,
        service: form.service || null,
        message: form.message,
      });
      toast.success("JazakAllah khair! Your inquiry has been received.");
      setForm({ name: "", email: "", service: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const infoLines = c?.info_lines || ["786i.net", "Serving the Ummah, worldwide"];

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 lg:py-32 bg-[#EFECE1]">
      <div
        className="absolute inset-0 opacity-[0.04] mix-blend-multiply pointer-events-none"
        style={{
          backgroundImage:
            "url(https://images.pexels.com/photos/34256958/pexels-photo-34256958.jpeg?auto=compress&w=800)",
          backgroundSize: "420px",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 items-start">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={fadeUp}>
          <p className="uppercase tracking-[0.35em] text-xs text-[#A9842F] mb-6 flex items-center gap-4">
            <span className="w-12 h-px bg-[#C8A24A]/60" />
            {c?.eyebrow || "Get in touch"}
          </p>
          <h2
            data-testid="contact-heading"
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-[#122A1D] leading-[1.05]"
          >
            {c?.heading || "Let's build something"}{" "}
            <em className="text-[#A9842F]">{c?.heading_accent || "beautiful"}</em>{" "}
            {c?.heading_suffix || "together"}
          </h2>
          <p className="mt-8 text-[#5B6F64] font-light leading-relaxed max-w-md">
            {c?.blurb ||
              "Whether it's a masjid app, an Islamic learning platform, or a full-scale web product — tell us your vision and we'll bring it to life with care and excellence."}
          </p>
          <div className="mt-10 space-y-3 text-sm text-[#5B6F64]">
            {infoLines.map((line, i) => (
              <p key={i} className="flex items-center gap-3">
                <span className="w-6 h-px bg-[#C8A24A]/60" />
                {line}
              </p>
            ))}
          </div>
        </motion.div>

        <motion.form
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          onSubmit={submit}
          data-testid="contact-form"
          className="bg-white border border-[#122A1D]/10 shadow-[0_12px_40px_rgba(18,42,29,0.08)] p-8 lg:p-10 space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <input
              data-testid="contact-input-name"
              required
              placeholder="Your name"
              value={form.name}
              onChange={set("name")}
              className={inputCls}
            />
            <input
              data-testid="contact-input-email"
              required
              type="email"
              placeholder="Email address"
              value={form.email}
              onChange={set("email")}
              className={inputCls}
            />
          </div>
          <select
            data-testid="contact-select-service"
            value={form.service}
            onChange={set("service")}
            className={`${inputCls} appearance-none cursor-pointer`}
          >
            <option value="">What do you need? (optional)</option>
            <option value="web">Web Application</option>
            <option value="mobile">Mobile Application</option>
            <option value="both">Web + Mobile</option>
            <option value="other">Something else</option>
          </select>
          <textarea
            data-testid="contact-input-message"
            required
            rows={5}
            placeholder="Tell us about your project..."
            value={form.message}
            onChange={set("message")}
            className={`${inputCls} resize-none`}
          />
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit-button"
            className="w-full bg-[#C8A24A] text-[#0B1F16] hover:bg-[#B8923D] disabled:opacity-60 font-semibold transition-colors duration-300 px-8 py-4 uppercase tracking-wider text-sm flex items-center justify-center gap-3"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {sending ? "Sending..." : "Send Inquiry"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
